"use client";

import Link from "next/link";
import { useActionState, useRef, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import { submitContact } from "../actions/contact";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const initialState = {
	success: false,
	error: null,
	message: null,
};

export default function ContactoPage() {
	const [state, formAction, isPending] = useActionState(submitContact, initialState);
	const [showPrivacyDetails, setShowPrivacyDetails] = useState(false);
	const formLoadedAt = useRef(Date.now().toString());

	return (
		<div className="font-sans min-h-screen bg-white text-text-primary flex flex-col">
			<Header />

			<main className="flex-grow">
				{/* Hero */}
				<section className="px-6 md:px-12 py-16 md:py-24 text-center">
					<h1 className="font-display text-4xl md:text-5xl font-medium text-text-primary leading-tight">
						Hablemos
					</h1>
					<p className="mt-6 text-xl text-text-secondary leading-relaxed max-w-xl mx-auto">
						¿Tienes dudas, gestionas varios locales o simplemente quieres saber más?{" "}
						<br className="hidden md:inline" />
						Cuéntanos y te respondemos con calma.
					</p>
				</section>

				{/* Formulario */}
				<section className="px-6 md:px-12 pb-24 md:pb-32">
					<div className="max-w-xl mx-auto">
						{state.success ? (
							<div className="bg-olive/10 border border-olive/30 rounded-2xl p-8 text-center">
								<div className="w-16 h-16 rounded-full bg-olive text-white flex items-center justify-center text-3xl mx-auto mb-6">
									✓
								</div>
								<h2 className="font-display text-2xl font-medium text-olive mb-4">
									¡Mensaje enviado!
								</h2>
								<p className="text-text-secondary text-lg leading-relaxed">
									{state.message}
								</p>
								<p className="mt-6 text-text-muted">
									Normalmente respondemos en menos de 24 horas.
								</p>
								<Link
									href="/"
									className="inline-block mt-8 text-olive hover:underline underline-offset-4"
								>
									Volver al inicio
								</Link>
							</div>
						) : (
							<form action={formAction} className="space-y-6">
								<input type="hidden" name="_loaded" value={formLoadedAt.current} />
								{/* Honeypot */}
								<div style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden="true">
									<label htmlFor="website">Website</label>
									<input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" />
								</div>

								{/* Error message */}
								{state.error && (
									<div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
										{state.error}
									</div>
								)}

								{/* Nombre */}
								<div>
									<label htmlFor="name" className="block text-text-primary font-medium mb-2">
										Tu nombre <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										id="name"
										name="name"
										required
										placeholder="¿Cómo te llamas?"
										className="w-full px-4 py-3 rounded-xl border border-border bg-white text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-olive/30 focus:border-olive transition-colors"
									/>
								</div>

								{/* Email */}
								<div>
									<label htmlFor="email" className="block text-text-primary font-medium mb-2">
										Email <span className="text-red-500">*</span>
									</label>
									<input
										type="email"
										id="email"
										name="email"
										required
										placeholder="tu@email.com"
										className="w-full px-4 py-3 rounded-xl border border-border bg-white text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-olive/30 focus:border-olive transition-colors"
									/>
								</div>

								{/* Motivo (opcional pero útil para priorizar) */}
								<div>
									<label htmlFor="reason" className="block text-text-primary font-medium mb-2">
										¿Sobre qué te gustaría hablar?
									</label>
									<select
										id="reason"
										name="reason"
										className="w-full px-4 py-3 rounded-xl border border-border bg-white text-text-primary focus:outline-none focus:ring-2 focus:ring-olive/30 focus:border-olive transition-colors appearance-none cursor-pointer"
										style={{
											backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23888888'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
											backgroundRepeat: "no-repeat",
											backgroundPosition: "right 1rem center",
											backgroundSize: "1.5em 1.5em",
											paddingRight: "3rem",
										}}
									>
										<option value="">Selecciona una opción (opcional)</option>
										<option value="varios-locales">Tengo varios locales o necesito más volumen</option>
										<option value="dudas">Tengo dudas sobre el servicio</option>
										<option value="otro">Otro motivo</option>
									</select>
								</div>

								{/* Mensaje */}
								<div>
									<label htmlFor="message" className="block text-text-primary font-medium mb-2">
										Tu mensaje <span className="text-red-500">*</span>
									</label>
									<textarea
										id="message"
										name="message"
										rows={5}
										required
										placeholder="Cuéntanos qué necesitas o qué te gustaría saber..."
										className="w-full px-4 py-3 rounded-xl border border-border bg-white text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-olive/30 focus:border-olive transition-colors resize-none"
									/>
								</div>

								{/* Política de privacidad */}
								<div className="bg-cream rounded-xl p-5">
									<div className="flex items-start gap-3">
										<input
											type="checkbox"
											id="acceptPrivacy"
											name="acceptPrivacy"
											value="yes"
											required
											className="mt-1 w-5 h-5 rounded border-border text-olive focus:ring-olive/30 cursor-pointer accent-olive"
										/>
										<label htmlFor="acceptPrivacy" className="text-text-secondary text-sm leading-relaxed cursor-pointer">
											He leído y acepto la{" "}
											<Link href="/privacidad" className="text-olive underline underline-offset-2">
												Política de Privacidad
											</Link>{" "}
											<span className="text-red-500">*</span>
										</label>
									</div>
									
									<button
										type="button"
										onClick={() => setShowPrivacyDetails(!showPrivacyDetails)}
										className="mt-3 text-text-muted text-xs flex items-center gap-1 hover:text-text-secondary transition-colors"
									>
										<span>{showPrivacyDetails ? "▼" : "▶"}</span>
										<span>Ver información sobre protección de datos</span>
									</button>
									
									{showPrivacyDetails && (
										<div className="mt-3 p-4 bg-white rounded-lg text-xs text-text-muted leading-relaxed space-y-2 border border-border">
											<p><strong>Responsable:</strong> Kaizen Feedback S.L.</p>
											<p><strong>CIF:</strong> ESB26818773</p>
											<p><strong>Dirección:</strong> Calle Gustavo Pérez Puig, 61. 28055. Madrid. España</p>
											<p><strong>Finalidad:</strong> Responder a tu consulta y mantenerte informado si lo solicitas.</p>
											<p><strong>Legitimación:</strong> Tu consentimiento al enviar este formulario.</p>
											<p><strong>Destinatarios:</strong> Los datos no se cederán a terceros salvo obligación legal.</p>
											<p><strong>Derechos:</strong> Acceso, rectificación, supresión, limitación, oposición y portabilidad. Puedes ejercerlos escribiendo a contacto@kaizenfeedback.com</p>
										</div>
									)}
								</div>

								{/* Turnstile */}
								{process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && (
									<Turnstile
										siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
										options={{ size: "flexible" }}
									/>
								)}

								{/* Submit button */}
								<div className="pt-4">
									<button
										type="submit"
										disabled={isPending}
										className="w-full rounded-full bg-olive text-white px-8 py-4 font-medium text-lg hover:bg-olive-light transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
									>
										{isPending ? (
											<span className="flex items-center justify-center gap-2">
												<span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
												Enviando...
											</span>
										) : (
											"Enviar mensaje"
										)}
									</button>
								</div>
							</form>
						)}
					</div>
				</section>

				{/* Alternativa: email directo */}
				<section className="px-6 md:px-12 py-16 bg-cream">
					<div className="max-w-xl mx-auto text-center">
						<p className="text-text-secondary">
							Si lo prefieres, también puedes escribirnos directamente a
						</p>
						<a 
							href="mailto:hola@kaizenfeedback.com" 
							className="inline-block mt-2 text-olive text-lg font-medium hover:underline underline-offset-4"
						>
							hola@kaizenfeedback.com
						</a>
					</div>
				</section>
			</main>

			<Footer />
		</div>
	);
}
